import { useTranslation } from '@kgc/i18n';
import { Checkbox } from '@kgc/ui';
import { ChevronsDownUp, ChevronsUpDown } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { INTERNAL_AUDIENCE, type DaforgeRelease } from '../data/daforge-data';
import { DaforgeReleaseGroup, type DaforgeFilters } from './DaforgeReleaseGroup';

interface DaforgeViewProps {
  /** Újabb kiadás elöl. */
  releases: DaforgeRelease[];
}

const changeKey = (releaseId: string, changeId: string) => `${releaseId}:${changeId}`;

/**
 * DaForge-kiadások összecsukható nézete: kiadás-csoport → változás → tesztesetek.
 * Alapból csak a legfrissebb kiadás nyitott, a változások csukottak (egy sor: típus ·
 * cím · terület · saját haladás). A nyitás-állapot itt él, hogy a „Mind kinyit" mindenre hasson.
 */
export function DaforgeView({ releases }: DaforgeViewProps) {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<DaforgeFilters>({
    area: '',
    onlyOpen: false,
    showInternal: false,
  });
  const [openReleases, setOpenReleases] = useState<Set<string>>(
    () => new Set(releases[0] ? [releases[0].release.id] : [])
  );
  const [openChanges, setOpenChanges] = useState<Set<string>>(() => new Set());

  const areas = useMemo(
    () => [...new Set(releases.flatMap(r => r.changes.map(c => c.area)))].sort(),
    [releases]
  );

  const toggleIn = useCallback((setter: typeof setOpenReleases, key: string) => {
    setter(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const expandAll = () => {
    setOpenReleases(new Set(releases.map(r => r.release.id)));
    setOpenChanges(
      new Set(
        releases.flatMap(r =>
          r.changes
            .filter(c => filters.showInternal || c.audience !== INTERNAL_AUDIENCE)
            .map(c => changeKey(r.release.id, c.id))
        )
      )
    );
  };
  const collapseAll = () => {
    setOpenReleases(new Set());
    setOpenChanges(new Set());
  };

  return (
    <div className="space-y-4" data-testid="daforge-view">
      {/* Szűrők */}
      <div className="flex flex-wrap items-center gap-4 rounded-lg border border-border p-3 text-sm">
        <label className="flex items-center gap-2">
          <span className="text-muted-foreground">{t('releaseHub.daforge.area')}</span>
          <select
            value={filters.area}
            onChange={e => setFilters(f => ({ ...f, area: e.target.value }))}
            className="rounded-md border border-border bg-background px-2 py-1"
            aria-label={t('releaseHub.daforge.area')}
          >
            <option value="">{t('releaseHub.daforge.allAreas')}</option>
            {areas.map(a => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2">
          <Checkbox
            checked={filters.onlyOpen}
            onCheckedChange={v => setFilters(f => ({ ...f, onlyOpen: v === true }))}
          />
          {t('releaseHub.daforge.onlyOpen')}
        </label>
        <label className="flex items-center gap-2">
          <Checkbox
            checked={filters.showInternal}
            onCheckedChange={v => setFilters(f => ({ ...f, showInternal: v === true }))}
          />
          {t('releaseHub.daforge.showInternal')}
        </label>
        <div className="ml-auto flex gap-1">
          <button
            onClick={expandAll}
            className="flex items-center gap-1 rounded px-2 py-1 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <ChevronsUpDown className="h-4 w-4" />
            {t('releaseHub.daforge.expandAll')}
          </button>
          <button
            onClick={collapseAll}
            className="flex items-center gap-1 rounded px-2 py-1 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <ChevronsDownUp className="h-4 w-4" />
            {t('releaseHub.daforge.collapseAll')}
          </button>
        </div>
      </div>

      {releases.map(rel => (
        <DaforgeReleaseGroup
          key={rel.release.id}
          release={rel}
          filters={filters}
          isOpen={openReleases.has(rel.release.id)}
          onToggle={() => toggleIn(setOpenReleases, rel.release.id)}
          isChangeOpen={changeId => openChanges.has(changeKey(rel.release.id, changeId))}
          onToggleChange={changeId => toggleIn(setOpenChanges, changeKey(rel.release.id, changeId))}
        />
      ))}
    </div>
  );
}
